import React from "react";
import LoadingIndicator from "src/pages/LoadingIndicator";
import FormPage from "../../components/FormPage/FormPage";
import {BASIC_INFORMATION_CONSTANTS as Labels} from "../../constants/LabelConstants";
import BasicDetailsSection from "../../forms/sections/BasicDetailsSection";
import {EstateItemTypes, FiduciarySummary, RevocableTrust} from "../../model/EstateFlowchartTypes";
import TrustDetailsSection from "../../forms/sections/TrustDetailsSection";
import {Will} from "../../model/Will";
import WillDetailsSection from "../../forms/sections/WillDetailsSection";
import {IrrevocableTrust} from "../../model/IrrevocableTrust";
import MiscellaneousDetailsSection from "../../forms/sections/MiscellaneousDetailsSection";
import {Miscellaneous} from "../../model/Miscellaneous";

type BasicInformationContentProps = {
    basicInformation: EstateItemTypes;
    displayTrustDetails?: boolean;
    updateEstateItem: (estateItem: EstateItemTypes) => void;
    formType?: "Will" | "RevocableTrust" | "IrrevocableTrust" | "Miscellaneous";
    pageIndex: number;
    fiduciaries?: FiduciarySummary;
    updateFiduciaries?: (fiduciarySummary: FiduciarySummary) => void;
}

/**
 * Component for rendering the basic information content of an estate flowchart.
 *
 * @component
 * @param {BasicInformationContentProps} props - The properties for the component.
 * @param {EstateItem} props.basicInformation - The estate item object
 * @param {Function} props.updateEstateItem - Function to update the estate item.
 * @param {number} props.pageIndex - The index of the page in the form
 *
 * @returns {JSX.Element} The rendered component.
 *
 * @example
 * <BasicInformationContent
 *   basicInformation={someTrust}
 *   updateEstateItem={handleUpdate}
 *   displayTrustDetails={true}
 *   pageIndex={0}
 * />
 *
 * @remarks
 * This component handles the display and update of basic information related to a revocable, irrevocable trust, or will.
 * It includes fields for flowchart name, legal name, nickname, date created, record of amendments,
 * grantors, trustees, funding, investment advisor, trust protector, and trust jurisdiction.
 *
 * The component uses various hooks such as `useState`, `useEffect`, and custom hooks like `useAppSelector`.
 * It also interacts with APIs to fetch family members and reference data.
 *
 * The component includes validation for required fields and dynamically updates the state based on user input.
 */
const BasicInformationContent: React.FC<BasicInformationContentProps> = ({
                                                                             basicInformation,
                                                                             updateEstateItem,
                                                                             displayTrustDetails = true,
                                                                             formType,
                                                                             pageIndex,
                                                                             fiduciaries,
                                                                             updateFiduciaries
                                                                         }: BasicInformationContentProps): JSX.Element => {
    if (!basicInformation) {
        return <LoadingIndicator/>;
    }

    return (
        <FormPage title={Labels.TITLE_LABEL_TEXT} pageIndex={pageIndex}>
            <BasicDetailsSection basicInformation={basicInformation}
                                 setBasicInformation={updateEstateItem}
                                 formType={formType}/>

            {displayTrustDetails &&
                <TrustDetailsSection estateItem={basicInformation as IrrevocableTrust | RevocableTrust}
                                     updateEstateItem={updateEstateItem}/>
            }
            {formType === 'Will' &&
                <WillDetailsSection estateItem={basicInformation as Will}
                                    updateEstateItem={updateEstateItem}
                                    fiduciaries={fiduciaries as FiduciarySummary}
                                    updateFiduciaries={updateFiduciaries!}/>
            }
            {formType === 'Miscellaneous' &&
                <MiscellaneousDetailsSection estateItem={basicInformation as Miscellaneous}
                                             updateEstateItem={updateEstateItem}/>
            }
        </FormPage>
    );
}

export default BasicInformationContent;